// Ссылка-приглашение показывается один раз (FR-003а; contracts/ui.md, «Окно «Новый
// пользователь»»): поле только для чтения, «Скопировать ссылку», подсказка, «Готово».
// То же окно — после сброса пароля и выпуска новой ссылки. Буфер обмена недоступен — тост
// с кодом интерфейса CLIP-001 (contracts/errors.md, «Коды интерфейса»; принцип V, T147).
import { clientErrorCatalog } from '@kp/contracts';
import type { ClientErrorCode, IssuedInvite } from '@kp/contracts';
import { Button, Modal, ModalFooter, TextField, useToast } from '@kp/ui';
import { Copy } from '@kp/ui/icons';
import { useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { formatDateTime } from '../../app/format.ts';

export const INVITE_ONCE_HINT =
  'Ссылку можно увидеть только сейчас. Закроете окно — выпустите новую в карточке';

/** Код интерфейса: браузер не дал записать в буфер обмена. */
export const CLIPBOARD_ERROR_CODE: ClientErrorCode = 'CLIP-001';

/** Тексты тостов копирования: удалось и не удалось (причина и шаг). */
export interface CopyTexts {
  done: string;
  failed: string;
}

const LINK_COPY: CopyTexts = {
  done: 'Ссылка скопирована',
  failed: `${clientErrorCatalog[CLIPBOARD_ERROR_CODE].message}. ${clientErrorCatalog[CLIPBOARD_ERROR_CODE].nextStep}`,
};

/**
 * Копирует ссылку; тост «Ссылка скопирована» — короткий, 2 с. Не вышло — ссылка в поле
 * выделяется, тост-ошибка с причиной, шагом и кодом CLIP-001. 035: те же правила у пароля
 * на «Задайте пароль» — со своими словами (texts), код тот же.
 */
export function useCopyText(
  texts: CopyTexts = LINK_COPY,
): (text: string, field?: HTMLInputElement | null) => Promise<void> {
  const toast = useToast();
  return useCallback(
    async (text: string, field?: HTMLInputElement | null) => {
      try {
        await navigator.clipboard.writeText(text);
        toast.ok(texts.done, { duration: 'short' });
      } catch {
        // Буфер обмена недоступен — выделяем текст в поле, его можно скопировать вручную.
        field?.focus();
        field?.select();
        toast.error(texts.failed, { code: CLIPBOARD_ERROR_CODE, duration: 'long' });
      }
    },
    [toast, texts],
  );
}

/** Поле со ссылкой; при показе получает фокус и выделение. */
export function InviteLinkField({
  url,
  inputRef,
}: {
  url: string;
  inputRef: RefObject<HTMLInputElement | null>;
}) {
  useEffect(() => {
    const field = inputRef.current;
    if (!field) return;
    field.focus();
    field.select();
  }, [inputRef, url]);
  return (
    <TextField
      ref={inputRef}
      label="Ссылка-приглашение"
      value={url}
      readOnly
      hint={INVITE_ONCE_HINT}
      onFocus={(event) => event.currentTarget.select()}
      inputClassName="kp-link-input"
    />
  );
}

/** «Ссылка действует до 17.10.2026 15:42». */
export function inviteValidity(invite: IssuedInvite, timeZone: string): string {
  return `Ссылка действует до ${formatDateTime(invite.expiresAt, timeZone)}`;
}

export interface InviteLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  invite: IssuedInvite | null;
  returnFocusRef?: RefObject<HTMLElement | null>;
}

/** Окно со ссылкой после сброса пароля и выпуска новой ссылки. */
export function InviteLinkModal({
  open,
  onOpenChange,
  title,
  description,
  invite,
  returnFocusRef,
}: InviteLinkModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const copy = useCopyText();
  return (
    <Modal
      open={open && invite !== null}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      returnFocusRef={returnFocusRef}
      footer={
        <ModalFooter
          secondary={
            <Button icon={Copy} onClick={() => invite && void copy(invite.url, inputRef.current)}>
              Скопировать ссылку
            </Button>
          }
          cancelLabel={null}
          primary={
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              Готово
            </Button>
          }
        />
      }
    >
      {invite ? <InviteLinkField url={invite.url} inputRef={inputRef} /> : null}
    </Modal>
  );
}
